import React, { useState, useRef, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTooltipPosition } from '../../hooks/useTooltipPosition';

export interface TooltipProps {
  content: ReactNode;
  variant?: 'default' | 'top' | 'bottom' | 'left' | 'right';
  offset?: number;
  className?: string;
  /** Extra classes for the hover-target wrapper (e.g. `w-full`). */
  triggerClassName?: string;
  children: ReactNode;
}

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  variant = 'default',
  offset,
  className = '',
  triggerClassName = '',
  children,
}) => {
  const triggerRef = useRef<HTMLElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const tooltipStyle = useTooltipPosition({
    triggerRef,
    tooltipRef,
    variant,
    offset,
    isVisible: showTooltip,
    content,
  });

  // Always use a wrapper span as the hover target so the tooltip works even when
  // the child is a disabled input/button (disabled elements don't fire pointer events).
  const trigger = (
    <span
      ref={(node) => {
        triggerRef.current = node;
      }}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onClick={() => setShowTooltip(false)}
      className={`inline-flex items-center justify-center ${triggerClassName}`}
    >
      {children}
    </span>
  );

  const allowsWrapping =
    className.includes('whitespace-normal')
    || className.includes('break-words')
    || className.includes('max-w-');

  return (
    <>
      {trigger}
      {content && showTooltip && createPortal(
        <span
          ref={tooltipRef}
          className={`fixed px-2 py-1 text-xs text-dark-invirtus bg-dark-invirtus-soft rounded-md shadow-lg pointer-events-none transition-opacity duration-200 text-center z-[9999] ${allowsWrapping ? 'whitespace-normal break-words' : 'whitespace-nowrap'} ${className}`}
          style={tooltipStyle}
          role="tooltip"
        >
          {content}
        </span>,
        document.body
      )}
    </>
  );
};
